import { CameraDetail } from '@hakimo-ui/hakimo/types';
import {
  CanvasDimensions,
  getInitialZones,
  ImageResolution,
  rescaleZones,
  ZonePolygonData,
} from '@hakimo-ui/hakimo/ui-elements';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseZoneStateParams {
  camera: CameraDetail;
}

export function useZoneState({ camera }: UseZoneStateParams) {
  const [canvasDimensions, setCanvasDimensions] =
    useState<CanvasDimensions | null>(null);
  const [imageResolution, setImageResolution] =
    useState<ImageResolution | null>(null);
  const [zones, setZones] = useState<ZonePolygonData[]>([]);

  const prevCanvasDimensionsRef = useRef<CanvasDimensions | null>(null);
  const isInitializedRef = useRef(false);
  const initialZonesRef = useRef<ZonePolygonData[]>([]);
  const initialZoneIdsRef = useRef<Set<string>>(new Set());

  // Initialize zones from camera data
  useEffect(() => {
    if (!canvasDimensions || !imageResolution || isInitializedRef.current)
      return;

    const initialZones = getInitialZones(
      camera,
      imageResolution,
      canvasDimensions
    );
    setZones(initialZones);
    initialZonesRef.current = initialZones;
    initialZoneIdsRef.current = new Set(initialZones.map((z) => z.id));
    prevCanvasDimensionsRef.current = canvasDimensions;
    isInitializedRef.current = true;
  }, [camera, canvasDimensions, imageResolution]);

  // Rescale zones when canvas dimensions change
  useEffect(() => {
    if (
      !canvasDimensions ||
      !prevCanvasDimensionsRef.current ||
      !isInitializedRef.current
    )
      return;

    const prev = prevCanvasDimensionsRef.current;
    if (
      prev.width === canvasDimensions.width &&
      prev.height === canvasDimensions.height
    )
      return;

    const prevDimensions = prev;
    setZones((currentZones) =>
      rescaleZones(currentZones, prevDimensions, canvasDimensions)
    );
    initialZonesRef.current = rescaleZones(
      initialZonesRef.current,
      prevDimensions,
      canvasDimensions
    );
    prevCanvasDimensionsRef.current = canvasDimensions;
  }, [canvasDimensions]);

  const handleCanvasDimensionsChange = useCallback(
    (dimensions: CanvasDimensions) => setCanvasDimensions(dimensions),
    []
  );

  const handleImageResolutionChange = useCallback(
    (resolution: ImageResolution) => setImageResolution(resolution),
    []
  );

  return {
    zones,
    setZones,
    canvasDimensions,
    imageResolution,
    initialZonesRef,
    initialZoneIdsRef,
    handleCanvasDimensionsChange,
    handleImageResolutionChange,
  };
}
